/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material';
import SortableTableHead, { SortableTableHeadCell } from '../../../shared/components/SortableTableHead';
import { makeStyles } from '@mui/styles';
import SearchBox from '../../../shared/components/SearchBox';
import { ContentResponse, ContentQueryParams, SubCategoryType } from '../../../../data/types/content.interface';
import { AddIcon } from '../../../shared/components/AppIcons';
import { TagType } from '../../../../data/types/tags.interface';
import ContentRowForTag from '../../contentRowForTags';
import FilterModal from '../../filterModal';
import PersonService from '../services/person.services';
import AddPerson from './addPerson';
import Chips from '../../../shared/components/Chips';
import FinalFileContentDetails from '../../../content-creation/components/partB/finalFileContentDetails';
import { isEmpty } from 'lodash';
import usePaginatedContent from '../../../shared/components/usePaginatedContent';
import ContentsService from '../../../content-creation/services/contents.services';
import { useAuthentication } from '../../../auth/services/auth.provider';

const useStyles = makeStyles({
  header: {
    fontSize: '28px',
  },
  add: {
    height: '38px',
    fontFamily: 'Lato !important',
    fontSize: '18px !important',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '& .MuiButton-startIcon': {
      margin: '0px !important',
    },
  },
  button: {
    width: '100px',
    height: '38px',
    marginRight: '24px !important',
    border: '1px solid #F67570 !important',
    color: '#F67570 !important',
    '& path': {
      fill: '#F67570 !important',
    },
    '&.delete': {
      '& .MuiButton-startIcon': {
        marginRight: '0px',
      },
    },
  },
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: '#30343F',
      color: '#fff !important',
      fontSize: '17px',
      '& .MuiCheckbox-root': {
        color: '#fff !important',
      },
      '& .MuiTableSortLabel-root.Mui-active': {
        color: '#fff !important',
      },
      '& .MuiTableSortLabel-icon': {
        color: '#fff !important',
      },
    },
  },
  clearAll: {
    color: '#30343F !important',
    fontFamily: 'Lato !important',
    fontSize: '12px !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    lineHeight: 'normal !important',
  },
});

const ContentCollectionByPerson: React.FC = () => {
  const classes = useStyles();
  const { currentUser } = useAuthentication();
  const [columnHeader, setColumnHeader] = useState<SortableTableHeadCell<any>[]>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [list, setList] = useState<TagType[]>([]);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [assetId, setAssetId] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [selectedPersons, setSelectedPersons] = React.useState<TagType[]>([]);
  const [selectedType, setSelectedType] = useState<TagType[]>([]);
  const [selectedThemes, setSelectedThemes] = React.useState<TagType[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<SubCategoryType[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<SubCategoryType[]>([]);
  const {
    contentCollections,
    listFilterParams,
    setListFilterParams,
    totalCount,
    contentTypes,
    categories,
    subCategories,
  } = usePaginatedContent({ type: 'final', contentType: 'person' });

  const isAdminOrContentSpecialist = ['admin', 'content_specialist'].includes(currentUser?.role.name || '');

  const getAllPersons = async () => {
    const result = await PersonService.get();
    if (result) {
      setList(result);
    }
  };

  useEffect(() => {
    setColumnHeader([
      { id: 'personId', label: 'Person Id', numeric: true, sortable: false },
      { id: 'personName', label: 'Person Name', numeric: false, sortable: false },
      { id: 'contentType', label: 'Content type', numeric: false, sortable: false },
      { id: 'folder', label: 'Content Folder', numeric: false, sortable: true },
      { id: 'fileType', label: 'File Type', numeric: false, sortable: false },
      { id: 'filename', label: 'File Name', numeric: false, sortable: false },
      { id: 'category', label: 'Category', numeric: false, sortable: false },
      { id: 'subcategory', label: 'Sub Category', numeric: false, sortable: false },
    ]);
    getAllPersons();
  }, []);

  useEffect(() => {
    setSelectedPersons(listFilterParams.persons || []);
    setSelectedType(listFilterParams.assetType || []);
    setSelectedThemes(listFilterParams.themes || []);
    setSelectedCategory(listFilterParams.category || []);
    setSelectedSubCategory(listFilterParams.subCategory || []);
  }, [listFilterParams]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      setListFilterParams((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  }, [refresh]);

  const onSearchBoxSubmit = (q: string) => {
    const newParams = { ...listFilterParams, q, page: 1 };

    if (isEmpty(q)) {
      const { q: _, ...rest } = newParams;
      setListFilterParams(rest);
    } else {
      setListFilterParams(newParams);
    }
  };

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAppliedFilters = (params: ContentQueryParams) => {
    const props = { ...listFilterParams, ...params };
    props.persons = params.tags;
    delete props.tags;
    setListFilterParams(props);
    setOpenFilters(false);
  };

  const handleCloseFilters = () => {
    setOpenFilters(false);
    setListFilterParams({
      perPage: 50,
      page: 1,
    });
  };

  const rowClickHandler = (property: any) => {
    setOpenDetailsModal(true);
    setAssetId(property.id);
  };

  const fetchFilterCount = () => {
    return ContentsService.getAppliedFiltersCount(listFilterParams);
  };

  const updateAll = (newParams: any) => {
    if (newParams.hasOwnProperty('themes')) {
      listFilterParams.themes = newParams?.themes;
    }
    if (newParams.hasOwnProperty('subCategory')) {
      listFilterParams.subCategory = newParams?.subCategory;
    }
    if (newParams.hasOwnProperty('category')) {
      listFilterParams.category = newParams?.category;
    }
    if (newParams.hasOwnProperty('persons')) {
      listFilterParams.persons = newParams?.persons;
    }
    if (newParams.hasOwnProperty('assetType')) {
      listFilterParams.assetType = newParams?.assetType;
    }

    setListFilterParams({
      ...listFilterParams,
      page: 1,
    });
  };

  const handleCancel = () => {
    setSelectedPersons([]);
    setSelectedType([]);
    setSelectedThemes([]);
    setSelectedCategory([]);
    setSelectedSubCategory([]);
    handleCloseFilters();
  };

  return (
    <Grid2 container direction="column">
      <Grid2 container justifyContent="space-between" alignItems="center" height="84px">
        <Grid2 container pl={3}>
          <span className={classes.header}>Person</span>
        </Grid2>
        <Grid2 container justifyContent="flex-end" mr="10px">
          <Grid2 container direction="column">
            <FilterModal
              handleFilters={handleAppliedFilters}
              open={openFilters}
              anchorEl={anchorEl}
              handleClose={handleCloseFilters}
              tagType="person"
              list={list}
              contentTypes={contentTypes}
              categories={categories}
              subCategories={subCategories}
            />
          </Grid2>
          <Grid2 container>
            <SearchBox
              query={listFilterParams.q}
              onSubmit={onSearchBoxSubmit}
              isFiltersPresent
              width={180}
              filtersCount={fetchFilterCount()}
              setOpenFilters={setOpenFilters}
              setFiltersAnchorEl={handleFilterOpen}
              placeholder="Search file"
            />
          </Grid2>
          {isAdminOrContentSpecialist && (
            <Grid2 container>
              <Button
                size="small"
                className={classes.add}
                startIcon={<AddIcon viewBox="0 0 42 42" style={{ fontSize: '35px' }} />}
                onClick={() => setOpenAddModal(true)}
              >
                <span style={{ fontSize: 'h5', textTransform: 'none', marginRight: '10px' }}>Add Person</span>
              </Button>
            </Grid2>
          )}
        </Grid2>
      </Grid2>

      {fetchFilterCount() > 0 && listFilterParams && (
        <>
          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>
          <Grid2 container mb={1} height="45px" display="flex" alignItems="center" padding="10px 10px 10px 20px">
            <Chips
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              selectedSubCategory={selectedSubCategory}
              setSelectedSubCategory={setSelectedSubCategory}
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              selectedThemes={selectedThemes}
              setSelectedThemes={setSelectedThemes}
              selectedPersons={selectedPersons}
              setSelectedPersons={setSelectedPersons}
              updateAll={updateAll}
            />

            {fetchFilterCount() > 0 && (
              <Grid2 container>
                <Button size="small" onClick={handleCancel} className={classes.clearAll}>
                  <span style={{ fontSize: '12px', textTransform: 'none' }}>CLEAR ALL</span>
                </Button>
              </Grid2>
            )}
          </Grid2>
        </>
      )}

      <TableContainer className={classes.table} sx={{ overflow: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <SortableTableHead
            onRequestSort={() => null}
            order={'asc'}
            orderBy={''}
            headCells={columnHeader}
            headerVariant="subtitle2"
            checkboxSelection={false}
            tableAssetType="tags"
          />

          {contentCollections?.length > 0 ? (
            <TableBody sx={{ color: '#303340' }}>
              {contentCollections.map((content: ContentResponse) => (
                <ContentRowForTag
                  key={`content-${content.id}`}
                  content={content}
                  tagType="person"
                  onRowClicked={() => rowClickHandler(content)}
                />
              ))}
            </TableBody>
          ) : (
            <TableBody sx={{ color: '#303340' }}>
              <TableRow>
                <TableCell colSpan={8} align="center">
                  You have not yet created any collection under any persons.
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
        {totalCount > 0 && (
          <Grid2 container justifyContent="center" mt={2} mb={2}>
            <TablePagination
              component="div"
              count={totalCount}
              page={listFilterParams.page - 1}
              rowsPerPage={listFilterParams.perPage}
              onPageChange={(_, newPage) =>
                setListFilterParams(prev => ({
                  ...prev,
                  page: newPage + 1
                }))
              }
              onRowsPerPageChange={(e) =>
                setListFilterParams(prev => ({
                  ...prev,
                  perPage: parseInt(e.target.value, 10),
                  page: 1
                }))
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  marginTop: 2,
                },
              }}
            />
          </Grid2>
        )}

        {openAddModal && <AddPerson setOpenPopup={setOpenAddModal} />}

        {openDetailsModal && assetId && (
          <FinalFileContentDetails setOpenDetailsModal={setOpenDetailsModal} id={assetId} setRefresh={setRefresh} />
        )}
      </TableContainer>
    </Grid2>
  );
};

export default ContentCollectionByPerson;
